/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { getDragImage } from './dndLegendUtils';
import { useDnd } from '#hooks/PRIVATE_useVisDnd';
import { getItemInfo } from '#utils/PRIVATE_legendUtils/legendUtils';
import { LegendDndProps, DropDetail, LegendSectionData } from '#utils/UNSAFE_visTypes/legend';
import { LegendDragStartDetail } from '#UNSAFE_Legend/legend.type';
import { SectionalLegendDragStartDetail } from '#UNSAFE_SectionalLegend/sectionalLegend.type';
import { Dispatch, StateUpdater } from 'preact/hooks';

type Props<D, S, W> = {
  items?: D[];
  sections?: S[];
  setDragOver: Dispatch<StateUpdater<boolean | undefined>>;
  setDatatipDisabled: Dispatch<StateUpdater<boolean>>;
} & LegendDndProps<W, D>;

export const useLegendDnd = <
  K,
  D,
  W extends SectionalLegendDragStartDetail<D> | LegendDragStartDetail<D> | undefined,
  S extends LegendSectionData<K, D> | void = void
>({
  onDragStart,
  onDrag,
  onDragEnd,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  items,
  sections,
  itemDraggable,
  setDragOver,
  setDatatipDisabled
}: Props<D, S, W>) => {
  const draggable = itemDraggable !== undefined;

  const getDragContext = function (target: HTMLElement, event: DragEvent): W {
    //We search for the key using the target of the event
    const itemIdx = getItemInfo(target as HTMLElement)?.itemIdx;
    let sectionIdx;
    if (sections) {
      sectionIdx = getItemInfo(target as HTMLElement)?.sectionIdx;
    }
    let data;
    if (items) {
      data = itemIdx !== undefined ? items[itemIdx] : null;
      if (itemDraggable && data && itemDraggable(data)) {
        return {
          type: 'item',
          data: data,
          event
        } as W;
      }
    } else {
      data =
        sectionIdx !== undefined && itemIdx !== undefined
          ? sections![sectionIdx]?.items[itemIdx]
          : null;
      if (itemDraggable && data && itemDraggable(data)) {
        return {
          type: 'item',
          data: data,
          event,
          itemIndexPath: [sectionIdx, itemIdx]
        } as W;
      }
    }
    return undefined as W;
  };
  const getDropContext = function (event: DragEvent) {
    return { type: 'background', event } as DropDetail;
  };

  const showDropEffect = function () {
    setDragOver(true);
  };

  const showRejectedDropEffect = function () {
    setDragOver(false);
  };

  const clearDropEffect = function () {
    setDragOver(undefined);
  };

  const { dragProps, dropProps, content } = useDnd<W, DropDetail>({
    onDragStart,
    onDrag,
    onDragEnd,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
    getDragContext,
    getDropContext,
    getDragImage,
    setDatatipDisabled,
    showDropEffect,
    showRejectedDropEffect,
    clearDropEffect
  });

  return {
    draggable: draggable,
    dndProps: draggable ? { ...dragProps, ...dropProps } : dropProps,
    dndContent: content
  };
};
